import { EditIcon, TrashIcon } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FormDetailPane } from '@/components/common/FormDetailPane';
import { SelectFormField, TextFormField } from '@/components/common/FormField';
import { Button } from '@/components/ui/button';
import { FieldGroup } from '@/components/ui/field';
import { handleApiError } from '../lib/errors';
import type { EmployeeCostGroupAssignment } from '../services/costGroupService';

export interface AssignmentForDrawer
  extends Pick<
    EmployeeCostGroupAssignment,
    'id' | 'employee_id' | 'cost_group' | 'calendar_year'
  > {}

interface EmployeeCostGroupAssignmentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  assignment?: AssignmentForDrawer | null;
  costGroups: { id: string; name: string }[];
  employees: { employee_id: string; name: string }[];
  /** Year to use when creating a new assignment (from parent's year filter). */
  selectedYear?: number | null;
  onSave: (
    payload: { cost_group: string; calendar_year: number },
    context?: { employeeId: string; assignmentId?: string }
  ) => undefined | Promise<unknown>;
  readOnly?: boolean;
  onEditRequest?: () => void;
  onDeleteRequest?: () => void;
}

const currentYear = new Date().getFullYear();

export const EmployeeCostGroupAssignmentDrawer: React.FC<
  EmployeeCostGroupAssignmentDrawerProps
> = ({
  isOpen,
  onClose,
  assignment = null,
  costGroups,
  employees,
  selectedYear = null,
  onSave,
  readOnly = false,
  onEditRequest,
  onDeleteRequest,
}) => {
  const isEdit = !!assignment?.id && !readOnly;
  const [cost_group, setCost_group] = useState('');
  const [employee_id, setEmployee_id] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCost_group(assignment?.cost_group ?? costGroups[0]?.name ?? '');
      setEmployee_id(assignment?.employee_id ?? employees[0]?.employee_id ?? '');
      setError(null);
      setSubmitError(null);
    }
  }, [
    isOpen,
    assignment?.id,
    assignment?.cost_group,
    assignment?.employee_id,
    costGroups,
    employees,
  ]);

  const employeeLabel = useMemo(() => {
    const emp = employees.find((e) => e.employee_id === employee_id);
    return emp ? `${emp.name} (${emp.employee_id})` : employee_id || 'N/A';
  }, [employees, employee_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const cg = cost_group.trim();
    const emp = employee_id.trim();
    if (!cg) {
      setError('Cost group is required');
      return;
    }
    if (!emp) {
      setError('Employee is required');
      return;
    }
    setError(null);
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const yearNum = isEdit
        ? assignment!.calendar_year
        : selectedYear != null
          ? selectedYear
          : currentYear;
      const payload: { cost_group: string; calendar_year: number } = {
        cost_group: cg,
        calendar_year: yearNum,
      };
      if (isEdit && assignment) {
        await Promise.resolve(
          onSave(payload, { employeeId: assignment.employee_id, assignmentId: assignment.id })
        );
      } else {
        await Promise.resolve(onSave(payload, { employeeId: emp }));
      }
      onClose();
    } catch (err) {
      setSubmitError(handleApiError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldsDisabled = readOnly;

  return (
    <FormDetailPane
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={readOnly ? 'View assignment' : isEdit ? 'Edit assignment' : 'Add assignment'}
      description={
        readOnly
          ? 'Employee cost group assignment details.'
          : isEdit
            ? 'Update cost group and employee.'
            : 'Select cost group and employee.'
      }
      onSubmit={readOnly ? undefined : handleSubmit}
      submitLabel={isEdit ? 'Update' : 'Add assignment'}
      isSubmitting={isSubmitting}
      submitError={submitError}
      showFooter={!readOnly}
    >
      {readOnly ? (
        <div className="flex flex-wrap justify-end gap-2 border-b border-border-default pb-4">
          {onEditRequest ? (
            <Button type="button" size="sm" onClick={onEditRequest}>
              <EditIcon data-icon="inline-start" />
              Edit
            </Button>
          ) : null}
          {onDeleteRequest ? (
            <Button type="button" size="sm" variant="destructive" onClick={onDeleteRequest}>
              <TrashIcon data-icon="inline-start" />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
      <FieldGroup>
        {readOnly ? (
          <>
            <TextFormField
              label="Employee"
              required
              value={employeeLabel}
              onChange={() => {}}
              disabled
              copyable
            />
            <TextFormField
              label="Cost group"
              required
              value={cost_group}
              onChange={() => {}}
              disabled
              copyable
            />
            <TextFormField
              label="Calendar year"
              required
              value={assignment?.calendar_year != null ? String(assignment.calendar_year) : 'N/A'}
              onChange={() => {}}
              disabled
              copyable
            />
          </>
        ) : (
          <>
            <SelectFormField
              label="Cost group"
              required
              value={cost_group}
              onChange={(v) => {
                setCost_group(v);
                if (error) setError(null);
              }}
              options={costGroups.map((g) => ({ value: g.name, label: g.name }))}
              placeholder="Select cost group"
              error={error === 'Cost group is required' ? error : undefined}
              disabled={fieldsDisabled}
            />
            <SelectFormField
              label="Employee"
              required
              value={employee_id}
              onChange={(v) => {
                setEmployee_id(v);
                if (error) setError(null);
              }}
              options={employees.map((emp) => ({
                value: emp.employee_id,
                label: `${emp.name} (${emp.employee_id})`,
              }))}
              placeholder="Select employee"
              error={error === 'Employee is required' ? error : undefined}
              disabled={fieldsDisabled}
            />
          </>
        )}
      </FieldGroup>
    </FormDetailPane>
  );
};
